import { useState, useCallback } from 'react'
import type { FormData } from '@/types/apply'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const URL_RE = /^https?:\/\/.+/i

type Errors = Record<string, string>

function validateStep1(data: FormData): Errors {
  const errors: Errors = {}
  const p = data.personal_info
  if (p.first_name.trim().length < 2) errors.first_name = 'First name must be at least 2 characters'
  if (p.last_name.trim().length < 2) errors.last_name = 'Last name must be at least 2 characters'
  if (!EMAIL_RE.test(p.email.trim())) errors.email = 'Please enter a valid email address'
  if (p.phone.replace(/\D/g, '').length < 5) errors.phone = 'Please enter a valid phone number'
  if (p.website.trim() && !URL_RE.test(p.website.trim())) errors.website = 'Please enter a valid URL'
  return errors
}

function validateStep2(data: FormData): Errors {
  const errors: Errors = {}
  const a = data.address
  if (a.street_address.trim().length < 5) errors.street_address = 'Street address must be at least 5 characters'
  if (a.city.trim().length < 2) errors.city = 'City is required'
  if (a.state.trim().length < 2) errors.state = 'State/Province is required'
  if (a.postal_code.trim().length < 3) errors.postal_code = 'Postal code is required'
  if (!a.country) errors.country = 'Please select a country'
  return errors
}

function validateStep3(data: FormData): Errors {
  const errors: Errors = {}
  const r = data.role_info
  if (!r.role) {
    errors.role = 'Please select whether you are an artist or label'
    return errors
  }
  if (r.role === 'artist' && r.artist_name.trim().length < 2) {
    errors.artist_name = 'Artist name must be at least 2 characters'
  }
  if (r.role === 'label') {
    if (r.label_name.trim().length < 2) {
      errors.label_name = 'Label name must be at least 2 characters'
    }
    // Required for labels (server enforces 10–1000 chars). Mirror it client-side
    // so labels get an inline error instead of a failed submission.
    if (r.artists_under_management.trim().length < 5) {
      errors.artists_under_management = 'List at least one artist you manage, with a Spotify or Instagram link (min 5 characters)'
    }
  }
  return errors
}

function validateStep4(data: FormData): Errors {
  const errors: Errors = {}
  const validLinks = data.demo_links.filter(l => l.trim())
  if (validLinks.length === 0) {
    errors.demo_links = 'At least one demo link is required'
    return errors
  }
  data.demo_links.forEach((link, i) => {
    if (link.trim() && !URL_RE.test(link.trim())) {
      errors[`demo_link_${i}`] = 'Please enter a valid URL'
    }
  })
  return errors
}

function validateStep5(data: FormData): Errors {
  const errors: Errors = {}
  const s = data.social_media
  const hasAny = s.instagram.trim() || s.tiktok.trim() || s.twitter.trim() || s.facebook.trim()
  if (!hasAny) {
    errors.social_media = 'At least one social media link is required'
  }
  return errors
}

function validateStep6(data: FormData): Errors {
  const errors: Errors = {}
  if (!data.subscription.plan) errors.plan = 'Please select a plan'
  if (!data.subscription.billing_cycle) errors.billing_cycle = 'Please select a billing cycle'
  return errors
}

const validators: Record<number, (data: FormData) => Errors> = {
  1: validateStep1,
  2: validateStep2,
  3: validateStep3,
  4: validateStep4,
  5: validateStep5,
  6: validateStep6
}

export function useValidation() {
  const [errors, setErrors] = useState<Errors>({})

  const validateStep = useCallback((step: number, formData: FormData): boolean => {
    const validator = validators[step]
    if (!validator) return true
    const stepErrors = validator(formData)
    setErrors(stepErrors)
    return Object.keys(stepErrors).length === 0
  }, [])

  const clearError = useCallback((field: string) => {
    setErrors(prev => {
      const next = { ...prev }
      delete next[field]
      return next
    })
  }, [])

  const clearAllErrors = useCallback(() => {
    setErrors({})
  }, [])

  return { errors, validateStep, clearError, clearAllErrors }
}
